import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  cn,
} from "@erp/ui";
import { Eye, EyeOff } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormSubmit,
  useZodForm,
} from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { queryKeys } from "@/lib/api/query";
import {
  AUTH_CODES,
  challengeSessionMFA,
  currentSession,
  login,
  type MfaMethod,
} from "@/lib/api/session";
import { formatDateTime } from "@/lib/format";

// Messages come from the forms kit's localized zod error map
// (min(1) → validation.required).
import { AuthLayout } from "./auth-layout";

// An account is named by its code, not by a mailbox: the code is unique
// across companies, so it is the whole identity a session is opened with.
const loginSchema = z.object({
  code: z.string().min(1).max(32),
  password: z.string().min(1),
});

export type LoginValues = z.infer<typeof loginSchema>;

type LoginError =
  | { kind: "invalidCredentials" }
  | { kind: "locked"; until: string | null }
  | { kind: "rateLimited" }
  | { kind: "generic" };

function toLoginError(error: unknown): LoginError {
  if (error instanceof ApiError) {
    if (error.code === AUTH_CODES.invalidCredentials) return { kind: "invalidCredentials" };
    if (error.code === AUTH_CODES.accountLocked || error.status === 423) {
      const extensions = error.problem as { locked_until?: unknown } | undefined;
      const until = typeof extensions?.locked_until === "string" ? extensions.locked_until : null;
      return { kind: "locked", until };
    }
    if (error.code === AUTH_CODES.rateLimited || error.status === 429) {
      return { kind: "rateLimited" };
    }
  }
  return { kind: "generic" };
}

interface StatusPanelProps {
  tone: "info" | "warning" | "destructive";
  children: string;
}

function StatusPanel({ tone, children }: StatusPanelProps) {
  return (
    <div
      role={tone === "info" ? "status" : "alert"}
      className={cn(
        "rounded-md border px-3 py-2 text-sm",
        tone === "info" && "border-border bg-muted text-muted-foreground",
        tone === "warning" && "border-warning bg-warning/10 text-foreground",
        tone === "destructive" && "border-destructive/50 bg-destructive/10 text-destructive",
      )}
    >
      {children}
    </div>
  );
}

const REMEMBERED_CODE_KEY = "erp.login.code";

/** The last user code, when the operator asked this browser to keep it. Only
 *  the code: a password is never written anywhere by this screen. */
function readRememberedCode(): string | null {
  try {
    const stored = window.localStorage.getItem(REMEMBERED_CODE_KEY);
    return stored === null || stored.trim() === "" ? null : stored;
  } catch {
    return null;
  }
}

function rememberCodeFor(code: string | null): void {
  try {
    if (code === null || code.trim() === "") {
      window.localStorage.removeItem(REMEMBERED_CODE_KEY);
      return;
    }
    window.localStorage.setItem(REMEMBERED_CODE_KEY, code.trim());
  } catch {
    // A browser that refuses storage still signs in; it just forgets.
  }
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const search = useSearch({ from: "/login" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [authError, setAuthError] = useState<LoginError | null>(null);
  const [revealed, setRevealed] = useState(false);
  // Operators sign in with a code they type every morning. Remembering it is
  // a convenience for the code alone — the password is never stored, so the
  // checkbox says exactly what it does.
  const [rememberCode, setRememberCode] = useState(() => readRememberedCode() !== null);
  // A password buys a challenge rather than a session when the account carries
  // a second factor. The pending session lives ten minutes and is held in the
  // cookie, so this screen only has to ask for the step.
  const [mfaMethods, setMfaMethods] = useState<readonly MfaMethod[] | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaPending, setMfaPending] = useState(false);

  const form = useZodForm(loginSchema, {
    defaultValues: { code: readRememberedCode() ?? "", password: "" },
  });

  const land = async () => {
    setAuthError(null);
    rememberCodeFor(rememberCode ? form.getValues().code : null);
    const session = await currentSession();
    queryClient.setQueryData(queryKeys.session.current(), session);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.session.all,
      refetchType: "none",
    });
    const target = search.redirect ?? "/";
    await navigate({ to: target as never });
  };

  const { onSubmit, isPending } = useFormSubmit(
    form,
    (values: LoginValues) => login(values.code, values.password),
    {
      onSuccess: async (outcome) => {
        // The password was right either way; a challenge is not a failure.
        if (outcome.kind === "mfa") {
          setAuthError(null);
          setMfaMethods(outcome.methods);
          return;
        }
        await land();
      },
      // Providing onError takes over global surfacing — errors render inline
      // instead of the kit's generic toast.
      onError: (error) => {
        setAuthError(toLoginError(error));
      },
    },
  );

  const errorPanel = useMemo(() => {
    if (!authError) return null;
    switch (authError.kind) {
      case "invalidCredentials":
        return <StatusPanel tone="destructive">{t("auth.invalidCredentials")}</StatusPanel>;
      case "locked": {
        let until = "";
        if (authError.until) {
          try {
            until = formatDateTime(authError.until, i18n.language);
          } catch {
            until = authError.until;
          }
        }
        return <StatusPanel tone="warning">{t("auth.accountLocked", { until })}</StatusPanel>;
      }
      case "rateLimited":
        return <StatusPanel tone="warning">{t("auth.rateLimited")}</StatusPanel>;
      case "generic":
        return <StatusPanel tone="destructive">{t("errors.generic")}</StatusPanel>;
    }
  }, [authError, i18n.language, t]);

  return (
    <AuthLayout>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>
            <h1>{t("auth.welcomeTitle", "Welcome back")}</h1>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("auth.welcomeSubtitle", "Sign in to your workspace")}
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {search.reason === "expired" && !authError && (
            <StatusPanel tone="info">{t("auth.sessionExpired")}</StatusPanel>
          )}
          {errorPanel}
          {mfaMethods === null ? null : (
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                setMfaPending(true);
                void challengeSessionMFA(mfaCode)
                  .then(async () => {
                    setMfaMethods(null);
                    setMfaCode("");
                    await land();
                  })
                  .catch((error: unknown) => setAuthError(toLoginError(error)))
                  .finally(() => setMfaPending(false));
              }}
            >
              <p className="text-sm text-muted-foreground">
                {t(
                  "auth.mfaPrompt",
                  "Enter the six digits from your authenticator app, or one recovery code. Either one works once.",
                )}
              </p>
              <label className="flex flex-col gap-1.5 text-sm" htmlFor="mfa-code">
                {t("auth.mfaCode", "Code")}
                <Input
                  id="mfa-code"
                  dir="ltr"
                  autoFocus
                  autoComplete="one-time-code"
                  className="font-mono tracking-widest"
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value.trim())}
                />
              </label>
              <Button type="submit" loading={mfaPending} className="w-full">
                {t("auth.mfaSubmit", "Continue")}
              </Button>
              <button
                type="button"
                className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                onClick={() => {
                  setMfaMethods(null);
                  setMfaCode("");
                }}
              >
                {t("auth.mfaBack", "Start over")}
              </button>
            </form>
          )}
          {mfaMethods !== null ? null : (
            <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("auth.userCode")}</FormLabel>
                    <FormControl>
                      <Input inputMode="text" autoComplete="username" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("auth.password")}</FormLabel>
                    {/* FormControl must wrap the input itself: it is what carries
                      the id the label points at, and a wrapper in between
                      leaves the label describing a div. */}
                    <div className="relative">
                      <FormControl>
                        <Input
                          type={revealed ? "text" : "password"}
                          autoComplete="current-password"
                          className="pe-10"
                          {...field}
                        />
                      </FormControl>
                      <button
                        type="button"
                        onClick={() => setRevealed((shown) => !shown)}
                        // The control is a button, not a checkbox: it changes
                        // nothing but what this screen shows.
                        aria-label={t(revealed ? "auth.hidePassword" : "auth.showPassword")}
                        aria-pressed={revealed}
                        className="absolute inset-y-0 end-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                      >
                        {revealed ? (
                          <EyeOff className="size-4" aria-hidden="true" />
                        ) : (
                          <Eye className="size-4" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={rememberCode}
                  onCheckedChange={(value) => {
                    const next = value === true;
                    setRememberCode(next);
                    if (!next) rememberCodeFor(null);
                  }}
                />
                {t("auth.rememberCode")}
              </label>
              <Button type="submit" loading={isPending} className="w-full">
                {t("actions.signIn")}
              </Button>
            </Form>
          )}
        </CardContent>
        <CardFooter>
          <Link
            to="/forgot-password"
            className="text-sm text-primary underline-offset-4 hover:underline"
          >
            {t("auth.forgotPassword")}
          </Link>
        </CardFooter>
      </Card>
    </AuthLayout>
  );
}
